import { useDebounce } from "client/hooks/utils/useDebounce";
import { useKeyboard } from "client/hooks/utils/useKeyboard";
import { isIos } from "client/utils/isIos";
import { useEffect, MouseEvent, useRef, TouchEvent, useState } from "react";

export const SKIP_TOUCH_ID_BOTTOM_MODAL = "SKIP_TOUCH_ID_BOTTOM_MODAL";

export const useBottomModal = (
    isOpen: boolean,
    lockBackground: boolean,
    minimumHeight?: number,
    onClose?: () => void,
) => {
    const childRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const bottomSectionRef = useRef<HTMLDivElement>(null);
    const [isMounted, setIsMounted] = useState(false);
    const mouseEndingPositionRef = useRef(0);
    useKeyboard("Escape", isOpen, () => {
        isOpen && onClose?.();
    });

    const handleClose = (_e: MouseEvent<HTMLDivElement>) => {
        if (isFocusedIn.current) return;
        _e.stopPropagation();
        onClose && onClose();
    };

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        e.stopPropagation();
        handleLockBackground();
    };

    const handleLockBackground = () => {
        if (!childRef.current) return;

        childRef.current.style.overflowY = "hidden";
        document.body.style.overflowY = "hidden";
        document.documentElement.style.margin = "0";
        document.documentElement.style.height = "100%";
        document.documentElement.style.overflow = "hidden";

        document.body.style.margin = "0";
        document.body.style.height = "100%";
        document.body.style.overflow = "hidden";
    };

    const handleUnlockBackground = () => {
        if (childRef.current) childRef.current.style.overflowY = "scroll";
        document.documentElement.style.overflow = "auto";
        document.body.style.overflow = "auto";
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        mouseEndingPositionRef.current = e.touches[0].clientY;
        e.stopPropagation();

        if (!childRef.current) return;

        const vh = getVh();

        if (vh <= 100) childRef.current.style.transform = `translateY(${100 - vh}vh)`;
    };

    const getVh = () => ((window.innerHeight - mouseEndingPositionRef.current) * 100) / window.innerHeight;

    const handleTouchEnd = (e: TouchEvent) => {
        // if any element has this id, it will skip it
        // TODO: this is bad as it'll fail if multiple items have the same id
        // it will fail in the second element's case
        if (document.getElementById(SKIP_TOUCH_ID_BOTTOM_MODAL)?.contains(e.target as Node)) return;
        if (!childRef.current || isFocusedIn.current) return;

        handleUnlockBackground();

        const vh = getVh();
        childRef.current.style.transform = `translateY(${100 - vh}vh)`;

        if (vh < 0) {
            onClose && onClose();
            return;
        }

        if (vh > 95) {
            childRef.current.style.transform = `translateY(0vh)`;
        }

        if (minimumHeight) {
            if (vhToPx(vh) < minimumHeight) {
                childRef.current.style.transform = `translateY(${100 - (minimumHeight < 25 ? 25 : minimumHeight)}vh)`;
            }
        } else {
            if (vh < 20) {
                childRef.current.style.transform = `translateY(100vh)`;
                onClose && onClose();
                return;
            }
        }

        contentRef.current && adjustContentHeight(contentRef.current);
    };

    const getWindowHeight = () => (typeof window !== "undefined" ? window.innerHeight : 0);

    const vhToPx = (vh: number) => (getWindowHeight() * vh) / 100;

    useEffect(() => {
        if (!lockBackground) return;

        if (isOpen) {
            // scrolls to the top of the page
            document.body.scrollTop = document.documentElement.scrollTop = 0;
            document.body.style.overflowY = "hidden";
            handleLockBackground();
            adjustMouseEndingPosition();
        } else {
            document.body.style.overflowY = "scroll";
        }
    }, [isOpen, lockBackground]);

    const adjustMouseEndingPosition = () => {
        if (!childRef.current) return;
        mouseEndingPositionRef.current = childRef.current.getBoundingClientRect().y;
    };

    const adjustContentHeight = (elem: HTMLElement) => {
        elem.style.height = `${
            window.innerHeight -
            elem.getBoundingClientRect().y -
            (bottomSectionRef.current?.getBoundingClientRect().height ?? 0)
        }px`;
    };

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        contentRef.current && adjustContentHeight(contentRef.current);
    }, [isOpen, contentRef.current]);

    const lastVhBeforeExpansion = useRef(0);
    const isFocusedIn = useRef(false);

    async function handleFocusIn(e: FocusEvent) {
        const el = e.target as HTMLElement;
        if (el.tagName !== "INPUT") return;
        isFocusedIn.current = true;
        if (getVh() < lastVhBeforeExpansion.current) {
            return;
        }

        lastVhBeforeExpansion.current = getVh();

        if (childRef.current) {
            childRef.current.style.transform = `translateY(0vh)`;
        }
    }

    function handleFocusOut(e: FocusEvent) {
        const el = e.target as HTMLElement;
        if (el.tagName !== "INPUT") return;
        const relatedTarget = e.relatedTarget as HTMLElement | undefined;
        if (relatedTarget && relatedTarget.tagName === "INPUT") {
            return;
        }
        isFocusedIn.current = false;

        if (childRef.current) {
            childRef.current.style.transform = `translateY(${100 - lastVhBeforeExpansion.current}vh)`;
        }

        contentRef.current && adjustContentHeight(contentRef.current);
    }

    useEffect(() => {
        mouseEndingPositionRef.current = childRef.current?.getBoundingClientRect().y ?? 0;
        lastWindowHeight.current = window.innerHeight;
    }, [childRef]);

    const lastWindowHeight = useRef(0);

    const resizeDebounce = useDebounce();
    function handleResize() {
        resizeDebounce(async () => {
            contentRef.current && adjustContentHeight(contentRef.current);

            lastWindowHeight.current = window.innerHeight;
        });
    }

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        if (!isIos()) {
            document.body.addEventListener("focusin", handleFocusIn);
            document.body.addEventListener("focusout", handleFocusOut);
        }

        return () => {
            window.removeEventListener("resize", handleResize);
            if (!isIos()) {
                document.body.removeEventListener("focusin", handleFocusIn);
                document.body.removeEventListener("focusout", handleFocusOut);
            }
        };
    }, []);

    return {
        handleClose,
        isMounted,
        childRef,
        handleTouchEnd,
        handleTouchMove,
        handleTouchStart,
        contentRef,
        bottomSectionRef,
    };
};
